import React, { memo } from 'react'

import { classnames, Filter, Icon } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, objectOf, oneOfType, shape, string } from 'prop-types'

import { iconSize, VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import useOpenVariation from 'Pods/ThreeDStudio/Hooks/useOpenVariation'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import useVariationMenu from 'Pods/ThreeDStudio/Hooks/useVariationMenu'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import { getVariationPreviewUrl, isRenderRun } from 'Pods/ThreeDStudio/Shared/Utils/renderRun'
import VariationInfo from 'Pods/ThreeDStudio/Shared/VariationInfo/VariationInfo'
import getSourceItemUrl from 'Pods/ThreeDStudio/Shared/VariationMenu/getSourceItemUrl'
import VariationMenu from 'Pods/ThreeDStudio/Shared/VariationMenu/VariationMenu'

import { NOOP } from 'Utils/stableDefaults'

import Media from './Components/Media/Media'

function Card({
  variation,
  position = null,
  isActive = false,
  isMain = false,
  isActionLoading = false,
  canOpenRelatedTab = false,
  showInfo = false,
  onSetAsMain: handleSetAsMain = NOOP,
  onDelete: handleDelete = NOOP,
}) {
  const t = useTranslation()
  const { id, providerModelId, title, prompt, createdAt } = variation
  const sourceItemUrl = getSourceItemUrl(variation)
  const previewUrl = getVariationPreviewUrl(variation)
  const variationType = VARIATION_TYPES[providerModelId]
  const isRender = isRenderRun(variation)
  const itemName = variationType?.label ?? title ?? prompt
  const { handleClick, handleKeyDown, handleOpenRelatedTab } = useOpenVariation(variation)
  const {
    shouldCloseDropdown,
    setShouldCloseDropdown,
    isMenuOpen,
    setIsMenuOpen,
    isDeleteModalOpen,
    handleSetAsMainClick,
    handleOpenDelete,
    handleCloseDelete,
    handleConfirmDelete,
  } = useVariationMenu({ id, itemName, onSetAsMain: handleSetAsMain, onDelete: handleDelete })

  const media = (
    <Media
      previewUrl={previewUrl}
      alt={title ?? prompt ?? variationType?.label ?? 'Variation preview'}
      variationType={variationType}
      isMain={isMain}
      isCover={isRender}
      position={position}
    >
      {canOpenRelatedTab && variationType && !isRender && (
        <button
          type="button"
          className="variation-card__related-tab"
          aria-label={t('viewerSidebar.enhance.variations.openRelatedTab')}
          onClick={handleOpenRelatedTab}
        >
          <Icon notSpaced svgComponent={Filter} color="dark" size={iconSize} />
        </button>
      )}
      <VariationMenu
        className="variation-card__dropdown-trigger"
        createdAt={createdAt}
        sourceItemUrl={sourceItemUrl}
        canSetAsMain={!isRender}
        isDisabled={isActionLoading}
        shouldClose={shouldCloseDropdown}
        setShouldClose={setShouldCloseDropdown}
        onOpenChange={setIsMenuOpen}
        onSetAsMain={handleSetAsMainClick}
        onDelete={handleOpenDelete}
      />
    </Media>
  )

  return (
    <>
      <div
        className={classnames(
          'variation-card',
          showInfo && 'variation-card--with-info',
          isActive && 'variation-card--active',
          isMenuOpen && 'variation-card--menu-open',
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        {showInfo ? (
          <>
            <div className="variation-card__media">{media}</div>
            <VariationInfo variation={variation} />
          </>
        ) : media}
      </div>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="variation"
          itemName={itemName}
          isSubmitDisabled={isActionLoading}
          onSubmit={handleConfirmDelete}
          onCancel={handleCloseDelete}
        />
      )}
    </>
  )
}

Card.propTypes = {
  variation: shape({
    id: oneOfType([string, number]).isRequired,
    slug: string,
    providerModelId: string,
    outputAssets: arrayOf(shape({
      previewUrl: string,
    })),
    title: string,
    prompt: string,
    createdAt: string,
    inputAssets: arrayOf(shape({
      id: number,
    })),
    sourceItems: objectOf(shape({
      url: string,
    })),
  }).isRequired,
  position: number,
  isActive: bool,
  isMain: bool,
  isActionLoading: bool,
  canOpenRelatedTab: bool,
  showInfo: bool,
  onSetAsMain: func,
  onDelete: func,
}

export default memo(Card)
